// src/pages/admin/Settings.jsx - Current structure (No changes needed)
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Settings as SettingsIcon, Building, Pill, Map, MapPin, Users2, CreditCard, DollarSign, Calendar, Tag, Smartphone } from 'lucide-react';

// Import settings components
import GrossisteSettings from '../../components/admin/settings/GrossisteSettings';
import PharmacieSettings from '../../components/admin/settings/PharmacieSettings';
import RegionsSettings from '../../components/admin/settings/RegionsSettings';
import VilleVillageSettings from '../../components/admin/settings/VilleVillageSettings';
import AssociationSettings from '../../components/admin/settings/AssociationSettings';
import OrganismePaiementSettings from '../../components/admin/settings/OrganismePaiementSettings';
import ParamsSettings from '../../components/admin/settings/ParamsSettings';
import EvenementSettings from '../../components/admin/settings/EvenementSettings';
import ProductCategoriesSettings from '../../components/admin/settings/ProductCategoriesSettings';
import AppConfigSettings from '../../components/admin/settings/AppConfigSettings';

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tab } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [animateContent, setAnimateContent] = useState(false);

  const tabs = [
    { name: 'Grossiste', icon: <Building className="w-5 h-5" />, color: 'text-blue-500', path: 'grossistes' },
    { name: 'Pharmacie', icon: <Pill className="w-5 h-5" />, color: 'text-green-500', path: 'pharmacies' },
    { name: 'Régions', icon: <Map className="w-5 h-5" />, color: 'text-red-500', path: 'regions' },
    { name: 'Ville/Village', icon: <MapPin className="w-5 h-5" />, color: 'text-teal-500', path: 'villes' },
    { name: 'Organisme de Paiement', icon: <CreditCard className="w-5 h-5" />, color: 'text-yellow-500', path: 'organismes' },
    { name: 'Seuils de Paiement', icon: <DollarSign className="w-5 h-5" />, color: 'text-emerald-500', path: 'params' },
    { name: 'Événements', icon: <Calendar className="w-5 h-5" />, color: 'text-violet-500', path: 'evenements' },
    { name: 'Association', icon: <Users2 className="w-5 h-5" />, color: 'text-pink-500', path: 'associations' },
    { name: 'Catégories Produits', icon: <Tag className="w-5 h-5" />, color: 'text-orange-500', path: 'categories' },
    { name: 'Application mobile', icon: <Smartphone className="w-5 h-5" />, color: 'text-blue-500', path: 'app' },
  ];

  // Set the active tab based on the URL parameter
  useEffect(() => {
    if (tab) {
      const tabIndex = tabs.findIndex(t => t.path === tab);
      if (tabIndex >= 0) {
        setActiveTab(tabIndex);
      }
    }
    setAnimateContent(true);
  }, [tab, tabs]);

  // Handler for tab change with animation
  const handleTabChange = (index) => {
    setAnimateContent(false);
    setTimeout(() => {
      setActiveTab(index);
      // Update URL to reflect the selected tab
      navigate(`/admin/settings/${tabs[index].path}`);
      setAnimateContent(true);
    }, 150);
  };

  // Function to render the active tab content with error handling
  const renderTabContent = () => {
    try {
      switch(activeTab) {
        case 0:
          return GrossisteSettings ? <GrossisteSettings /> : <div>Component not available</div>;
        case 1:
          return PharmacieSettings ? <PharmacieSettings /> : <div>Component not available</div>;
        case 2:
          return RegionsSettings ? <RegionsSettings /> : <div>Component not available</div>;
        case 3:
          return VilleVillageSettings ? <VilleVillageSettings /> : <div>Component not available</div>;
        case 4:
          return OrganismePaiementSettings ? <OrganismePaiementSettings /> : <div>Component not available</div>;
        case 5:
          return ParamsSettings ? <ParamsSettings /> : <div>Component not available</div>;
        case 6:
          return EvenementSettings ? <EvenementSettings /> : <div>Component not available</div>;
        case 7:
          return AssociationSettings ? <AssociationSettings /> : <div>Component not available</div>;
        case 8:
          return ProductCategoriesSettings ? <ProductCategoriesSettings /> : <div>Component not available</div>;
        case 9:
          return AppConfigSettings ? <AppConfigSettings /> : <div>Component not available</div>;
        default:
          return <div>Select a tab</div>;
      }
    } catch (error) {
      console.error("Error rendering tab content:", error);
      return <div>Error loading component. Check console for details.</div>;
    }
  };

  return (
    <div className="settings-page pb-8">
      <div className="mb-8 bg-gradient-to-r from-green-700 to-emerald-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center">
          <SettingsIcon className="w-8 h-8 mr-3" />
          <div>
            <h1 className="text-2xl font-bold">Paramètres</h1>
            <p className="text-green-100">
              Gérez les configurations système et les entités de la plateforme
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Side Navigation on larger screens */}
        <div className="lg:col-span-3 hidden lg:block">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <h3 className="font-medium text-gray-800">Catégories</h3>
            </div>
            <nav className="p-2">
              {tabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => handleTabChange(index)}
                  className={`w-full flex items-center px-4 py-3 rounded-md mb-1 text-left transition-colors ${
                    activeTab === index
                      ? 'bg-green-50 text-green-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className={`mr-3 ${tab.color}`}>{tab.icon}</span>
                  <span className="text-sm">{tab.name}</span>
                  {activeTab === index && (
                    <div className="ml-auto w-1.5 h-6 bg-green-700 rounded-full"></div>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-9">
          {/* Horizontal Tab Navigation (Mobile & Tablet) */}
          <div className="lg:hidden mb-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto whitespace-nowrap p-1 scrollbar-hide">
                <div className="inline-flex space-x-1 min-w-full">
                  {tabs.map((tab, index) => (
                    <button
                      key={index}
                      onClick={() => handleTabChange(index)}
                      className={`flex-shrink-0 inline-flex items-center px-3 py-2 rounded-md text-xs ${
                        activeTab === index
                          ? 'bg-green-100 text-green-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className={`mr-1 ${tab.color}`}>{tab.icon}</span>
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div 
            className={`transition-opacity duration-150 ease-in-out ${
              animateContent ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="bg-white rounded-lg shadow-md">
              <div className="p-4 border-b border-gray-200 flex items-center">
                <span className={`mr-2 ${tabs[activeTab].color}`}>{tabs[activeTab].icon}</span>
                <h2 className="text-xl font-medium text-gray-800">
                  {tabs[activeTab].name}
                </h2>
              </div>
              <div className="p-4">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;